import React from 'react';
import { useNode, BuilderComponent, getFontStyle } from '@mnz/builder';
import Box from '@mui/material/Box';

import StyledComponent from '../../core/StyledComponent';
import ImgTag from '../../core/ImgTag';
import { BasicInfoSetting } from './BasicInfoSetting';
import { px2rem } from '../../core';
import { BasicInfoSettingProps } from './BasicInfoSetting';
import ImageIcon from '@menuzen/icon/build/outline/ImageIcon';
import { getBorderCSS, hoverAnimationCss } from '../../common/style';

export const BasicInfo: BuilderComponent<BasicInfoSettingProps> = ({
    designSettings,
    account,
    image,
    hiddenBasicInfoItems,
}) => {
    const {
        connectors: { connect, drag },
        hidden,
        query: { getOptions },
    } = useNode((node) => ({
        hidden: node.data.hidden,
    }));
    const canEdit = getOptions().enabled;

    if (hidden) return <></>;

    return (
        <StyledComponent ref={(ref) => connect(drag(ref))}>
            <StyledComponent xs={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {(!hiddenBasicInfoItems?.COVER_IMAGE || !hiddenBasicInfoItems?.LOGO) && (
                    <Box
                        sx={{
                            width: '100%',
                            ...hoverAnimationCss({
                                css: {
                                    borderTopLeftRadius: '10px',
                                    borderTopRightRadius: '10px',
                                },
                                contentPosition: 'BOT',
                                content: '"Cover Image"',
                                active: canEdit,
                                hoverCss: { color: (theme) => theme.palette.success.main },
                            }),
                        }}
                    >
                        <Box
                            sx={{
                                height: 120,
                                width: '100%',
                                borderBottom:
                                    canEdit &&
                                    !designSettings.coverImage &&
                                    !hiddenBasicInfoItems?.COVER_IMAGE &&
                                    'dashed #fff',
                            }}
                        >
                            {designSettings.coverImage && !hiddenBasicInfoItems?.COVER_IMAGE && (
                                <ImgTag
                                    src={designSettings.coverImage}
                                    alt="coverImage"
                                    xs={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                )}
                {!hiddenBasicInfoItems?.LOGO && (image || canEdit) && (
                    <Box
                        sx={{
                            marginTop: '-60px',
                            ...hoverAnimationCss({
                                content: '"Logo"',
                                active: canEdit,
                                hoverCss: { color: (theme) => theme.palette.success.main },
                            }),
                        }}
                    >
                        <Box
                            sx={{
                                height: 120,
                                width: 120,
                                ...getBorderCSS({
                                    corner: designSettings.styles.corner,
                                    buttonStyle: designSettings.styles.button,
                                    maxBorderRadius: 1,
                                }),
                            }}
                        >
                            {image && (
                                <ImgTag
                                    src={image}
                                    alt="Logo"
                                    xs={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: 'inherit',
                                    }}
                                />
                            )}

                            {!image && canEdit && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        color: '#ABAEB2',
                                        cursor: 'pointer',
                                        backgroundColor: '#fff',
                                        borderRadius: 'inherit',
                                        fontSize: '14px',
                                        fontFamily: (theme) => theme.typography.fontFamily,
                                        border: '1px solid #EFEFF0',
                                        transitionProperty: 'color, border-color !important',
                                    }}
                                >
                                    <StyledComponent
                                        xs={{
                                            fontSize: 20,
                                            marginBottom: 3,
                                        }}
                                    >
                                        <ImageIcon />
                                    </StyledComponent>
                                    <StyledComponent
                                        xs={{
                                            fontWeight: 600,
                                        }}
                                    >
                                        Add Image
                                    </StyledComponent>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {account.name && !hiddenBasicInfoItems?.BUSINESS_NAME && (
                    <Box
                        sx={{
                            color: designSettings.colors.text.color,
                            fontSize: px2rem(38),
                            textAlign: 'center',
                            marginX: '40px',
                            ...getFontStyle(designSettings, 'primary'),
                            ...hoverAnimationCss({ content: '"Business Name"', active: canEdit }),
                        }}
                    >
                        {account.name}
                    </Box>
                )}

                {account.about && !hiddenBasicInfoItems?.BUSINESS_DESCRIPTION && (
                    <Box
                        component="h1"
                        sx={{
                            color: designSettings.colors.text.color,
                            marginTop: '5px',
                            marginX: '35px',
                            fontSize: px2rem(18),
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                            ...getFontStyle(designSettings, 'secondary'),
                            ...hoverAnimationCss({ content: '"Business Description"', active: canEdit }),
                        }}
                    >
                        {account.about}
                    </Box>
                )}
            </StyledComponent>
        </StyledComponent>
    );
};

BasicInfo.defaultProps = {
    hiddenBasicInfoItems: {},
};

BasicInfo.settings = {
    displayName: 'Basic Info',
    related: {
        settings: BasicInfoSetting,
    },
    rules: {
        canDrag: () => false,
    },
    custom: {
        showBox: false,
    },
};
